import { useCallback, useEffect, useRef, useState } from 'react';

import {
	APP_UPDATE_REMIND_AFTER_MS,
	APP_VERSION_CHECK_INTERVAL_MS,
	APP_VERSION_FOCUS_CHECK_MIN_GAP_MS,
	APP_VERSION_URL,
	CURRENT_APP_VERSION,
} from '../config';

const RELOADED_FOR_VERSION_KEY = 'appUpdateReloadedForVersion';

// Reloads at most once per deployed version — guards against a reload loop if a cache in front
// of the server still serves the old index.html while version.json is already new.
const reloadOnceForVersion = (version) => {
	try {
		if (sessionStorage.getItem(RELOADED_FOR_VERSION_KEY) === version) return;
		sessionStorage.setItem(RELOADED_FOR_VERSION_KEY, version);
	} catch {
		return; // storage blocked — can't guard against a loop, keep the notice instead
	}
	window.location.reload();
};

// Detects that a newer build was deployed while this tab is open:
//   - polls /version.json every APP_VERSION_CHECK_INTERVAL_MS and when the tab becomes visible
//     again, and compares it with the build id baked into this bundle;
//   - also reacts to `vite:preloadError` — a lazily loaded route chunk that no longer exists on
//     the server is the typical symptom of running an outdated bundle after a deploy. The failed
//     import can also be a network blip, so the version is checked first; only a confirmed new
//     deploy reloads the page (once per deploy, see reloadOnceForVersion), which is the recovery
//     for the navigation that just failed. The event is not cancelled: preventDefault would only
//     make the lazy route resolve to undefined and fail anyway, so the router's errorElement
//     handles the failed navigation as before.
// Disabled in dev (no version.json there). Network errors are ignored; the next check retries.
//
// @returns {{ isVisible: boolean, dismiss: () => void }}
//   isVisible — a new version exists and the user has not dismissed the notice recently
//   dismiss   — hides the notice for APP_UPDATE_REMIND_AFTER_MS
export const useAppUpdateCheck = () => {
	const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
	const [isDismissed, setIsDismissed] = useState(false);
	const lastCheckRef = useRef(0);

	const isEnabled = !import.meta.env.DEV && Boolean(CURRENT_APP_VERSION);

	// Resolves with the deployed version when it differs from this bundle, otherwise null.
	const checkVersion = useCallback(async () => {
		lastCheckRef.current = Date.now();
		try {
			const response = await fetch(`${APP_VERSION_URL}?t=${Date.now()}`, { cache: 'no-store' });
			if (!response.ok) return null;
			const { version } = await response.json();
			if (!version || version === CURRENT_APP_VERSION) return null;
			setIsUpdateAvailable(true);
			return version;
		} catch {
			// offline or a deploy in progress — try again on the next tick
			return null;
		}
	}, []);

	useEffect(() => {
		if (!isEnabled || isUpdateAvailable) return;

		const intervalId = setInterval(checkVersion, APP_VERSION_CHECK_INTERVAL_MS);

		const handleVisibilityChange = () => {
			if (document.visibilityState !== 'visible') return;
			if (Date.now() - lastCheckRef.current < APP_VERSION_FOCUS_CHECK_MIN_GAP_MS) return;
			checkVersion();
		};
		document.addEventListener('visibilitychange', handleVisibilityChange);

		return () => {
			clearInterval(intervalId);
			document.removeEventListener('visibilitychange', handleVisibilityChange);
		};
	}, [isEnabled, isUpdateAvailable, checkVersion]);

	useEffect(() => {
		if (!isEnabled) return;

		const handlePreloadError = async () => {
			const deployedVersion = await checkVersion();
			if (deployedVersion) reloadOnceForVersion(deployedVersion);
		};
		window.addEventListener('vite:preloadError', handlePreloadError);
		return () => window.removeEventListener('vite:preloadError', handlePreloadError);
	}, [isEnabled, checkVersion]);

	useEffect(() => {
		if (!isDismissed) return;
		const timeoutId = setTimeout(() => setIsDismissed(false), APP_UPDATE_REMIND_AFTER_MS);
		return () => clearTimeout(timeoutId);
	}, [isDismissed]);

	const dismiss = useCallback(() => setIsDismissed(true), []);

	return { isVisible: isUpdateAvailable && !isDismissed, dismiss };
};
